import {
    ContentDetail,
    Post,
    File,
    Article,
    SharedDocument,
    SharedImage,
    SharedLink,
    SharedVideo,
    ImageCarousel,
    PodcastItem,
    PostAttachment,
    Video,
    Audio,
    Thread,
    PostAuthorInfo,
    SharedPost,
    Website,
    Email,
    ImageAsset,
    Image,
    Book,
    ContentTraverser,
    CustomAudio,
    CustomVideo,
    PlanSettings
} from "@authory-packages/core";
import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { VerticalSpacing } from "../VerticalSpacing";
import { V3AudioPlayer } from "../AudioPlayer";
import Link from "next/link";
import { enhanceIframesAndBuiltInVideos } from "./utils";
import { VideoViewer } from "../VideoViewer";
import { V3VideoPlayer } from "../VideoPlayer";
import { V3ArticleView } from "../ReusableStyledComponents/V3ArticleView";
import { V3HeadlineH4 } from "../Typography";
import { V3CaptionGrey48 } from "../ReusableStyledComponents/V3CaptionGrey48";
import {
    ArticleLink,
    AuthoryMuxWrapper,
    BlockQuote,
    MediaImage,
    PostWrapper,
    ThreadContainer,
    ThreadHandler,
    ThreadIconCount,
    ThreadIconWrapper,
    ThreadPositioner,
    ThreadWrapper,
    TweeterProfileAvatar,
    TweeterProfileComponent,
    V3MediaImageWrapper,
    V3PostAttachmentWrapper,
    ContentTextColumn
} from "./V3ContentRenderer.styles";
import { V3BodyGrey48 } from "../ReusableStyledComponents/V3BodyGrey48";
import isHtml from "is-html";
import { ImageVisualizer } from "../ImageVisualizer";
import { UrlParser } from "@authory-packages/core";
import { nofollowLinks } from "../../utils/nofollowLinks";
import { decodeEntities } from "../../utils/decodeHTMLEntities";
import { getSignedPlaybackToken } from "../../api/manual-import";
import { V3Loader } from "../Loader";
import { useTheme } from "styled-components";
import { isAxiosError } from "../../utils/toError";
import { Errors } from "../../types/api";
import { canUseProFeature } from "../../utils/canUseProFeature";
import { MediaPlaybackUnavailableVariant, V3MediaPlaybackUnavailable } from "../V3EmptyStateScreen/V3MediaPlaybackUnavailable";

/**
 * Prepares stored content html for rendering.
 * @param html The stored content html
 * @param enhance Whether to wrap iframes and enable video controls, only after mount so hydration matches the server html
 * @returns The html to render
 */
function prepareHTMLForRender(html: string, enhance: boolean) {
    const sanitizedHTML = nofollowLinks(html);

    if (enhance) {
        return enhanceIframesAndBuiltInVideos(sanitizedHTML);
    }

    return sanitizedHTML;
}

// PDF Viewer is only rendered on client side
const PDFViewer = dynamic(() => import("../PDFViewer/PDFViewer"), { ssr: false });
const MuxPlayer = dynamic(() => import("@mux/mux-player-react"), { ssr: false });
const PodcastItemHtml = dynamic(() => import("./PodcastItemHtml"));

interface V3ContentRendererProps {
    content: ContentDetail;
    ownerPlanSettings?: PlanSettings;
}

export const V3ContentRenderer: React.FC<React.PropsWithChildren<V3ContentRendererProps>> = ({ content, ownerPlanSettings }) => {
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    return <>{new RecursiveContentRenderer(isMounted, ownerPlanSettings).traverse(content)}</>;
};

const AttachmentFirstTypes = [PostAttachment.video, PostAttachment.image, PostAttachment.imageCarousel];

type PlaybackTokenStatus = "loading" | "ready" | "limitReached" | "unavailable";

/**
 * Requests a signed Mux playback token for an uploaded file.
 * @param uploadId The file upload id
 * @returns The token and the request status, the API answers 403 once the owner's monthly playback allowance is used up
 */
function useSignedPlaybackToken(uploadId: string | undefined) {
    const [signedToken, setSignedToken] = useState<string | undefined>(undefined);
    const [status, setStatus] = useState<PlaybackTokenStatus>("loading");

    useEffect(() => {
        if (!uploadId) return;

        const getToken = async () => {
            try {
                const signedTokenRes = await getSignedPlaybackToken({ uploadId });
                setSignedToken(signedTokenRes);
                setStatus("ready");
            } catch (error) {
                setStatus(isAxiosError(error) && error.response?.status === Errors.AccessDenied ? "limitReached" : "unavailable");
            }
        };

        getToken();
    }, [uploadId]);

    return { signedToken, status };
}

/**
 * Picks the unavailable message, only the owner learns about the playback limit and their plan.
 * @param limitReached Whether the API reported the playback limit
 * @param ownerPlanSettings The owner's plan, set only when the owner is viewing
 * @returns The variant to render
 */
function getPlaybackUnavailableVariant(limitReached: boolean, ownerPlanSettings?: PlanSettings) {
    if (!limitReached || !ownerPlanSettings) return MediaPlaybackUnavailableVariant.Visitor;

    return canUseProFeature(ownerPlanSettings) ? MediaPlaybackUnavailableVariant.OwnerContact : MediaPlaybackUnavailableVariant.OwnerUpgrade;
}

const CustomMediaPlayer: React.FC<{
    uploadId: string | null;
    playbackId: string | null;
    html: string;
    enhanceHtml: boolean;
    audio?: boolean;
    ownerPlanSettings?: PlanSettings;
}> = ({ uploadId, playbackId, html, enhanceHtml, audio, ownerPlanSettings }) => {
    const { signedToken, status } = useSignedPlaybackToken(uploadId ?? undefined);
    const [metadataLoaded, setMetadataLoaded] = useState(false);
    const theme = useTheme();

    return (
        <div>
            <VerticalSpacing top={84}>
                <V3ArticleView dangerouslySetInnerHTML={{ __html: prepareHTMLForRender(html, enhanceHtml) }} suppressHydrationWarning />
            </VerticalSpacing>
            <VerticalSpacing top={48}>
                {!uploadId || !playbackId ? (
                    <></>
                ) : (
                    <>
                        {status === "loading" ? (
                            <V3Loader />
                        ) : status === "ready" && signedToken ? (
                            <AuthoryMuxWrapper $audio={!!audio} style={{ visibility: metadataLoaded ? "visible" : "hidden" }}>
                                <MuxPlayer
                                    tokens={{
                                        playback: signedToken
                                    }}
                                    onLoadedMetadata={() => setMetadataLoaded(true)}
                                    playbackId={playbackId}
                                    audio={audio}
                                    accentColor={audio ? undefined : theme.customization.accentcolour}
                                />
                            </AuthoryMuxWrapper>
                        ) : (
                            <V3MediaPlaybackUnavailable
                                variant={getPlaybackUnavailableVariant(status === "limitReached", ownerPlanSettings)}
                                audio={audio}
                            />
                        )}
                    </>
                )}
            </VerticalSpacing>
        </div>
    );
};

class RecursiveContentRenderer extends ContentTraverser<React.ReactNode> {
    constructor(
        private readonly enhanceHtml: boolean,
        private readonly ownerPlanSettings?: PlanSettings
    ) {
        super();
    }

    private handlePostInternal(post: Post, attachmentResult: React.ReactNode[]): React.ReactNode {
        const stringIsHTML = isHtml(post.html);

        if (post.attachment && !Array.isArray(post.attachment) && AttachmentFirstTypes.includes(post.attachment?.type)) {
            return (
                <PostWrapper>
                    <VerticalSpacing bottom={24}>{attachmentResult}</VerticalSpacing>
                    <V3ArticleView>
                        {stringIsHTML ? (
                            <div dangerouslySetInnerHTML={{ __html: prepareHTMLForRender(post.html, this.enhanceHtml) }} suppressHydrationWarning />
                        ) : (
                            <p dangerouslySetInnerHTML={{ __html: prepareHTMLForRender(post.html, this.enhanceHtml) }} suppressHydrationWarning />
                        )}
                    </V3ArticleView>
                </PostWrapper>
            );
        } else if (!Array.isArray(post.attachment) && post.attachment?.type === PostAttachment.link) {
            let domain = "";

            try {
                domain = UrlParser.extractDomain(post.attachment.href) || "";
            } catch {
                domain = "";
            }

            return (
                <PostWrapper>
                    <VerticalSpacing bottom={20}>
                        <V3ArticleView>
                            {stringIsHTML ? (
                                <div
                                    dangerouslySetInnerHTML={{ __html: prepareHTMLForRender(post.html, this.enhanceHtml) }}
                                    suppressHydrationWarning
                                />
                            ) : (
                                <p dangerouslySetInnerHTML={{ __html: prepareHTMLForRender(post.html, this.enhanceHtml) }} suppressHydrationWarning />
                            )}
                        </V3ArticleView>
                    </VerticalSpacing>
                    <Link href={post.attachment.href} passHref legacyBehavior>
                        <ArticleLink target="_blank">
                            <V3PostAttachmentWrapper>
                                <VerticalSpacing bottom={8}>
                                    {post.attachment.previewImageUrl && (
                                        <V3MediaImageWrapper>
                                            <img src={post.attachment.previewImageUrl} />
                                        </V3MediaImageWrapper>
                                    )}
                                </VerticalSpacing>
                                {!!domain.length && (
                                    <VerticalSpacing bottom={8}>
                                        <V3CaptionGrey48>{domain}</V3CaptionGrey48>
                                    </VerticalSpacing>
                                )}
                                <V3HeadlineH4>{post.attachment.text}</V3HeadlineH4>
                            </V3PostAttachmentWrapper>
                        </ArticleLink>
                    </Link>
                </PostWrapper>
            );
        } else {
            return (
                <PostWrapper>
                    <V3ArticleView>
                        {stringIsHTML ? (
                            <div dangerouslySetInnerHTML={{ __html: prepareHTMLForRender(post.html, this.enhanceHtml) }} suppressHydrationWarning />
                        ) : (
                            <p dangerouslySetInnerHTML={{ __html: prepareHTMLForRender(post.html, this.enhanceHtml) }} suppressHydrationWarning />
                        )}
                    </V3ArticleView>
                    {attachmentResult}
                </PostWrapper>
            );
        }
    }

    handleFile(file: File): React.ReactNode {
        return (
            <>
                <ContentTextColumn>
                    <VerticalSpacing bottom={24}>
                        <V3ArticleView
                            dangerouslySetInnerHTML={{ __html: prepareHTMLForRender(file.html, this.enhanceHtml) }}
                            suppressHydrationWarning
                        />
                    </VerticalSpacing>
                </ContentTextColumn>
                <div>
                    <PDFViewer url={file.fileUrl} />
                </div>
            </>
        );
    }

    handlePost(post: Post, attachmentResult: React.ReactNode[], _postAuthorInfoResult: string | null): React.ReactNode {
        return <>{this.handlePostInternal(post, attachmentResult)}</>;
    }

    handlePostAuthorInfo(postAuthorInfo: PostAuthorInfo): string {
        return postAuthorInfo.displayName;
    }

    handleThread(thread: Thread) {
        return (
            <ThreadContainer>
                {thread.posts.map((post, index) => (
                    <ThreadWrapper isLast={index === thread.posts.length - 1} key={index}>
                        <ThreadPositioner>
                            {index === 0 ? (
                                <TweeterProfileComponent>
                                    {typeof thread.by !== "string" && (
                                        <>
                                            <TweeterProfileAvatar src={thread.by?.profileImageUrl} />
                                            <V3HeadlineH4>{this.handlePostAuthorInfo(thread.by)}</V3HeadlineH4>
                                            <ThreadHandler>{`@${thread.by.profileUrl?.split("/").pop()}`}</ThreadHandler>
                                        </>
                                    )}
                                </TweeterProfileComponent>
                            ) : (
                                <ThreadIconWrapper>
                                    <div>
                                        <ThreadIconCount>
                                            <div>
                                                <svg width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path
                                                        d="M4.728 11.64a.496.496 0 0 0 .318-.636.52.52 0 0 0-.656-.309l.338.945Zm7.218-.945a.52.52 0 0 0-.656.309c-.093.26.05.546.318.636l.338-.945Zm.69 1.64c0-.028.018.039-.18.18-.188.134-.493.275-.916.402-.84.253-2.033.417-3.372.417v1c1.413 0 2.714-.171 3.677-.461.48-.145.904-.326 1.22-.552.307-.217.603-.545.603-.987h-1.031Zm-4.468 1c-1.34 0-2.532-.165-3.372-.418-.423-.127-.728-.268-.916-.402-.198-.141-.18-.208-.18-.18H2.667c0 .441.296.769.602.986.317.226.742.407 1.22.552.965.29 2.265.461 3.678.461v-1Zm-4.469-1c0 .03.003-.07.218-.254.19-.163.472-.327.81-.441l-.337-.945a3.524 3.524 0 0 0-1.157.637c-.275.236-.565.588-.565 1.002h1.031Zm7.91-.695c.338.114.62.278.81.44.215.186.218.284.218.254h1.031c0-.414-.29-.766-.565-1.002a3.522 3.522 0 0 0-1.157-.637l-.338.945Z"
                                                        fill="#ABB0BA"
                                                    />
                                                    <path
                                                        d="M12.333 10.5a.51.51 0 0 0-.52-.5.51.51 0 0 0-.521.5h1.041Zm-7.291 0a.51.51 0 0 0-.521-.5.51.51 0 0 0-.521.5h1.042Zm6.25 0c0-.182.145-.2-.021-.11a2.727 2.727 0 0 1-.647.214c-.61.14-1.479.23-2.457.23v1c1.035 0 1.989-.095 2.698-.258.351-.08.671-.183.916-.315.203-.11.552-.347.552-.76h-1.041Zm-3.125.334c-.979 0-1.848-.09-2.458-.23a2.728 2.728 0 0 1-.646-.214c-.166-.09-.021-.072-.021.11H4c0 .414.35.651.552.76.245.133.565.236.916.316.71.163 1.664.258 2.699.258v-1ZM12.333 6.5a.51.51 0 0 0-.52-.5.51.51 0 0 0-.521.5h1.041Zm-7.291 0A.51.51 0 0 0 4.52 6 .51.51 0 0 0 4 6.5h1.042Zm6.25 0c0-.182.145-.2-.021-.11a2.732 2.732 0 0 1-.647.214c-.61.14-1.479.23-2.457.23v1c1.035 0 1.989-.095 2.698-.257.351-.08.671-.184.916-.316.203-.11.552-.347.552-.76h-1.041Zm-3.125.334c-.979 0-1.848-.09-2.458-.23a2.732 2.732 0 0 1-.646-.214c-.166-.09-.021-.072-.021.11H4c0 .414.35.651.552.76.245.133.565.236.916.317.71.162 1.664.257 2.699.257v-1Z"
                                                        fill="#ABB0BA"
                                                    />
                                                    <path
                                                        d="M12.333 6.5a.51.51 0 0 0-.52-.5.51.51 0 0 0-.521.5h1.041Zm-7.291 0A.51.51 0 0 0 4.52 6 .51.51 0 0 0 4 6.5h1.042Zm6.25 0c0-.182.145-.2-.021-.11a2.732 2.732 0 0 1-.647.214c-.61.14-1.479.23-2.457.23v1c1.035 0 1.989-.095 2.698-.257.351-.08.671-.184.916-.316.203-.11.552-.347.552-.76h-1.041Zm-3.125.334c-.979 0-1.848-.09-2.458-.23a2.732 2.732 0 0 1-.646-.214c-.166-.09-.021-.072-.021.11H4c0 .414.35.651.552.76.245.133.565.236.916.317.71.162 1.664.257 2.699.257v-1ZM12.333 8.5a.51.51 0 0 0-.52-.5.51.51 0 0 0-.521.5h1.041Zm-7.291 0A.51.51 0 0 0 4.52 8 .51.51 0 0 0 4 8.5h1.042Zm6.25 0c0-.182.145-.2-.021-.11a2.732 2.732 0 0 1-.647.214c-.61.14-1.479.23-2.457.23v1c1.035 0 1.989-.095 2.698-.257.351-.08.671-.184.916-.316.203-.11.552-.347.552-.76h-1.041Zm-3.125.334c-.979 0-1.848-.09-2.458-.23a2.732 2.732 0 0 1-.646-.214c-.166-.09-.021-.072-.021.11H4c0 .414.35.651.552.76.245.133.565.236.916.317.71.162 1.664.257 2.699.257v-1Z"
                                                        fill="#ABB0BA"
                                                    />
                                                    <path
                                                        d="M3.335 3.668C3.335 2.748 5.424 2 8 2c2.578 0 4.667.746 4.667 1.667 0 .92-2.09 1.666-4.667 1.666s-4.666-.746-4.666-1.666Z"
                                                        stroke="#ABB0BA"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                    <path stroke="#ABB0BA" strokeLinecap="round" d="M4.5 5.167v5.667M11.832 5.167v5.667" />
                                                    <ellipse cx="8" cy="3.434" rx="1" ry=".433" fill="#ABB0BA" />
                                                </svg>
                                            </div>
                                            <div>
                                                <V3BodyGrey48>{`${index + 1}/${thread.posts.length}`}</V3BodyGrey48>
                                            </div>
                                        </ThreadIconCount>
                                    </div>
                                </ThreadIconWrapper>
                            )}
                        </ThreadPositioner>
                        <div>{this.traversePost(post)}</div>
                    </ThreadWrapper>
                ))}
            </ThreadContainer>
        );
    }

    handleVideo(video?: Video): React.ReactNode {
        if (!video) return <></>;
        return <VideoViewer video={video} />;
    }

    handleCustomAudio(customAudio: CustomAudio): React.ReactNode {
        return (
            <CustomMediaPlayer
                uploadId={customAudio.uploadId}
                playbackId={customAudio.playbackId}
                html={customAudio.html}
                enhanceHtml={this.enhanceHtml}
                audio
                ownerPlanSettings={this.ownerPlanSettings}
            />
        );
    }

    handleCustomVideo(customVideo: CustomVideo): React.ReactNode {
        return (
            <CustomMediaPlayer
                uploadId={customVideo.uploadId}
                playbackId={customVideo.playbackId}
                html={customVideo.html}
                enhanceHtml={this.enhanceHtml}
                ownerPlanSettings={this.ownerPlanSettings}
            />
        );
    }

    handleArticle(article: Article): React.ReactNode {
        return <V3ArticleView dangerouslySetInnerHTML={{ __html: prepareHTMLForRender(article.html, this.enhanceHtml) }} suppressHydrationWarning />;
    }

    handleDocumentAttachment(attachment: SharedDocument): React.ReactNode {
        return (
            <V3ArticleView>
                <iframe src={attachment.src} title={attachment.title} />
            </V3ArticleView>
        );
    }

    handleImage(image: Image): React.ReactNode {
        return (
            <>
                <VerticalSpacing bottom={24}>
                    <V3ArticleView>
                        <div dangerouslySetInnerHTML={{ __html: prepareHTMLForRender(image.html, this.enhanceHtml) }} suppressHydrationWarning />
                    </V3ArticleView>
                </VerticalSpacing>
                <ImageVisualizer srcs={image.screenshots.map((img) => img.src)} />
            </>
        );
    }

    handleBook(book: Book): React.ReactNode {
        return (
            <VerticalSpacing bottom={24}>
                <V3ArticleView>
                    <div dangerouslySetInnerHTML={{ __html: prepareHTMLForRender(book.html, this.enhanceHtml) }} suppressHydrationWarning />
                </V3ArticleView>
            </VerticalSpacing>
        );
    }

    handleImageAttachment(attachment: SharedImage): React.ReactNode {
        return (
            <figure key={attachment.src}>
                <MediaImage src={attachment.src}></MediaImage>
            </figure>
        );
    }

    handleLinkAttachment(attachment: SharedLink): React.ReactNode {
        return (
            <>
                <figure>
                    {attachment.previewImageUrl ? (
                        <a href={attachment.href}>
                            <MediaImage src={attachment.previewImageUrl} />
                        </a>
                    ) : null}
                </figure>
                <a href={attachment.href}>{attachment.text}</a>
            </>
        );
    }

    handlePostAttachment(attachment: SharedPost): React.ReactNode {
        return <BlockQuote>{this.traversePost(attachment.post)}</BlockQuote>;
    }

    handleVideoAttachment(attachment: SharedVideo): React.ReactNode {
        return <V3VideoPlayer attachment={attachment} />;
    }

    handleImageCarouselAttachment(attachment: ImageCarousel): React.ReactNode {
        return (
            <>
                {attachment.srcs.map((image, index) => (
                    <figure key={index}>
                        <MediaImage src={image} />
                    </figure>
                ))}
            </>
        );
    }

    handlePodcastItem(podcastItem: PodcastItem): React.ReactNode {
        // Podcast HTML comes from RSS feeds entity-encoded (e.g. `&lt;div&gt;`), so it must be
        // decoded before injection, otherwise the tags render as literal text.
        const decodedHTML = decodeEntities(podcastItem.html);

        return (
            <>
                {podcastItem.media && !!podcastItem.media.url && (
                    <VerticalSpacing bottom={24}>
                        <V3AudioPlayer type={podcastItem.media.type} src={podcastItem.media.url} />
                    </VerticalSpacing>
                )}
                <PodcastItemHtml html={prepareHTMLForRender(decodedHTML, this.enhanceHtml)} />
            </>
        );
    }

    handleAudio(audio: Audio) {
        return (
            <>
                <VerticalSpacing bottom={24}>
                    <V3AudioPlayer type={audio.contentType} src={audio.audioUrl} />
                </VerticalSpacing>
                <V3ArticleView dangerouslySetInnerHTML={{ __html: prepareHTMLForRender(audio.html, this.enhanceHtml) }} suppressHydrationWarning />
            </>
        );
    }

    handleWebsite(website: Website) {
        const stringIsHTML = isHtml(website.description);

        return (
            <>
                <VerticalSpacing bottom={24}>
                    <V3ArticleView>
                        {stringIsHTML ? (
                            <div
                                dangerouslySetInnerHTML={{ __html: prepareHTMLForRender(website.description, this.enhanceHtml) }}
                                suppressHydrationWarning
                            />
                        ) : (
                            <p dangerouslySetInnerHTML={{ __html: website.description }} suppressHydrationWarning />
                        )}
                    </V3ArticleView>
                </VerticalSpacing>
                <ImageVisualizer srcs={website.screenshots.map((img) => img.src)} />
            </>
        );
    }

    handleEmail(email: Email) {
        const stringIsHTML = isHtml(email.description);
        return (
            <>
                <VerticalSpacing bottom={24}>
                    <V3ArticleView>
                        {stringIsHTML ? (
                            <div
                                dangerouslySetInnerHTML={{ __html: prepareHTMLForRender(email.description, this.enhanceHtml) }}
                                suppressHydrationWarning
                            />
                        ) : (
                            <p dangerouslySetInnerHTML={{ __html: email.description }} suppressHydrationWarning />
                        )}
                    </V3ArticleView>
                </VerticalSpacing>
                <ImageVisualizer srcs={email.screenshots.map((img) => img.src)} />
            </>
        );
    }

    // Frontend takes care of image on it's own via ImageVisualizer
    handleImageAsset(_image: ImageAsset) {
        return <div></div>;
    }
}
